// Lib
import React, { useState } from 'react';
import { DragLayerMonitor, useDragLayer } from 'react-dnd';
import { ConnectedProps, connect } from 'react-redux';
import classNames from 'classnames';

// Components
import renderThrottleDecorator from '../../../utils/milanoteRecompose/renderThrottleDecorator';

// Selector
import { getGridSize } from '../../../utils/grid/gridSizeSelector';
import { isAttachModeSelector } from '../../../reducers/draggingSelector';

// Types
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { DragProps } from '../dragTypes';
import LineDragPerformanceLayer from './LineDragPerformanceLayer';

const connector = connect((state) => ({
    gridSize: getGridSize(state),
    isAttachMode: isAttachModeSelector(state),
}));

type LineDragLayerContainerProps = ConnectedProps<typeof connector> & {
    currentBoardId: string;
    scrollDiff: Point;
};

const ORIGIN = { x: 0, y: 0 };

const lineDragLayerConnect = (monitor: DragLayerMonitor): DragProps => ({
    itemType: monitor.getItemType(),
    item: monitor.getItem(),
    offsetDiff: monitor.getDifferenceFromInitialOffset() || ORIGIN,
    initialClientOffset: monitor.getInitialClientOffset() || ORIGIN,
    initialSourceClientOffset: monitor.getInitialSourceClientOffset() || ORIGIN,
    // For a single frame after a drop, item above will be non-null while all the
    // offsets will be null. Rather than worry about null checks on all of those,
    // default to ORIGIN and store the dropping status here for components to check
    isDropping: !monitor.getDifferenceFromInitialOffset(),
});

const LineDragLayerContainer = ({
    currentBoardId,
    isAttachMode,
    scrollDiff,
    gridSize,
}: LineDragLayerContainerProps) => {
    const [initialCurrentBoardId] = useState(currentBoardId);

    const dragProps = useDragLayer(lineDragLayerConnect);

    if (!dragProps.item) return null;

    // At this stage, no connected lines should be shown when the current board changes
    if (initialCurrentBoardId !== currentBoardId) return null;

    return (
        <div className={classNames('LineDragLayer', { 'ws-is-attach-mode': isAttachMode })}>
            <LineDragPerformanceLayer {...dragProps} gridSize={gridSize} scrollDiff={scrollDiff} />
        </div>
    );
};

export default renderThrottleDecorator(connector(LineDragLayerContainer));
